import React from 'react';
import { Send, X, Sparkles, Mail, Settings } from 'lucide-react';
import skylarqLogo from '../../../assets/skylarq_logo_small.png';
import { CampaignData, ChatMessage } from './types';

interface CampaignChatPanelProps {
  isOpen: boolean;
  campaign: CampaignData;
  chatMessages: ChatMessage[];
  chatInput: string;
  isChatLoading: boolean;
  isTypingComplete: boolean;
  typingText: string;
  chatContainerRef: React.RefObject<HTMLDivElement>;
  chatInputRef: React.RefObject<HTMLInputElement>;
  onClose: () => void;
  onOpen: () => void;
  onInputChange: (value: string) => void;
  onSend: () => void;
  onEditEmail: () => void;
  onEditSettings: () => void;
}

const renderFormattedContent = (content: string) => {
  const parts = content.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-semibold text-neutral-900">{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
};

export const CampaignChatPanel: React.FC<CampaignChatPanelProps> = ({
  isOpen,
  campaign,
  chatMessages,
  chatInput,
  isChatLoading,
  isTypingComplete,
  typingText,
  chatContainerRef,
  chatInputRef,
  onClose,
  onOpen,
  onInputChange,
  onSend,
  onEditEmail,
  onEditSettings,
}) => {
  return (
    <>
      <style>{`
        @media (min-width: 640px) {
          .chat-panel-responsive {
            width: 380px !important;
            max-width: 380px !important;
            top: 0 !important;
          }
        }
      `}</style>
      <div
        className="chat-panel-responsive fixed top-14 md:top-0 left-0 right-0 bottom-0 sm:left-auto flex flex-col bg-white sm:border-l border-neutral-200 z-40"
        style={{
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 500ms cubic-bezier(0.16, 1, 0.3, 1)'
        }}
      >
        <div className="h-12 sm:h-16 flex items-center justify-between px-4 border-b border-neutral-200 flex-shrink-0 bg-white">
          <div className="flex items-center gap-2">
            <img src={skylarqLogo} alt="Skylarq" className="w-5 h-5 object-contain" />
            <span className="text-[12px] sm:text-[13px] font-medium text-neutral-900">Campaign Assistant</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onEditEmail}
              className="p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-md"
              title="Edit email template"
            >
              <Mail className="w-4 h-4" />
            </button>
            <button
              onClick={onEditSettings}
              className="p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-md"
              title="Campaign settings"
            >
              <Settings className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-md"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4">
          {chatMessages.length === 0 && !isChatLoading && (
            <div className="py-8">
              <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-5 h-5 text-neutral-400" />
              </div>
              <p className="text-[13px] text-neutral-600 text-center mb-1">
                Analyzing campaign...
              </p>
            </div>
          )}
          {chatMessages.map((msg, idx) => (
            <div key={msg.id} className={`mb-4 ${msg.role === 'user' ? 'flex justify-end' : ''}`}>
              {msg.role === 'assistant' && (
                <div className="flex gap-3 group">
                  <img
                    src={skylarqLogo}
                    alt=""
                    className="w-6 h-6 object-contain flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0 pt-0.5">
                    <p className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wide mb-1">SKYLARQ</p>
                    <div className="text-[14px] text-neutral-800 leading-relaxed whitespace-pre-wrap">
                      {msg.isTyping && idx === chatMessages.length - 1 && !isTypingComplete
                        ? renderFormattedContent(typingText)
                        : renderFormattedContent(msg.content)
                      }
                    </div>
                  </div>
                </div>
              )}
              {msg.role === 'user' && (
                <div className="max-w-[85%] bg-neutral-900 text-white rounded-2xl rounded-br-sm px-4 py-2.5">
                  <p className="text-[13px] leading-relaxed whitespace-pre-wrap">
                    {msg.content}
                  </p>
                </div>
              )}
            </div>
          ))}

          {isTypingComplete && chatMessages.length > 0 && chatMessages[chatMessages.length - 1].role === 'assistant' && (
            <div className="mt-4 mb-2 ml-9">
              <div className="flex flex-wrap gap-1.5">
                {campaign.recipients.filter(r => r.status === 'replied').length > 0 && (
                  <button
                    onClick={() => onInputChange('Help me respond to the reply')}
                    className="px-3 py-1.5 text-[11px] font-medium text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-full hover:bg-white hover:border-neutral-300 transition-colors"
                  >
                    Respond to reply
                  </button>
                )}
                {campaign.recipients.filter(r => r.status === 'clicked' && r.clickCount >= 2).length > 0 && (
                  <button
                    onClick={() => onInputChange('Draft a follow-up for interested leads')}
                    className="px-3 py-1.5 text-[11px] font-medium text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-full hover:bg-white hover:border-neutral-300 transition-colors"
                  >
                    Follow up
                  </button>
                )}
                <button
                  onClick={() => onInputChange('Any tips to get more opens?')}
                  className="px-3 py-1.5 text-[11px] font-medium text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-full hover:bg-white hover:border-neutral-300 transition-colors"
                >
                  Boost opens
                </button>
                <button
                  onClick={() => onInputChange('Can you suggest a better subject line?')}
                  className="px-3 py-1.5 text-[11px] font-medium text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-full hover:bg-white hover:border-neutral-300 transition-colors"
                >
                  Subject ideas
                </button>
              </div>
            </div>
          )}

          {isChatLoading && (
            <div className="flex gap-3">
              <img
                src={skylarqLogo}
                alt=""
                className="w-6 h-6 object-contain flex-shrink-0"
              />
              <div className="flex-1 min-w-0 pt-0.5">
                <p className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wide mb-1">SKYLARQ</p>
                <div className="flex items-center gap-1 py-1">
                  <span className="w-1.5 h-1.5 bg-neutral-300 rounded-full animate-pulse" />
                  <span className="w-1.5 h-1.5 bg-neutral-300 rounded-full animate-pulse" style={{ animationDelay: '0.15s' }} />
                  <span className="w-1.5 h-1.5 bg-neutral-300 rounded-full animate-pulse" style={{ animationDelay: '0.3s' }} />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-4 pb-4 pt-2 border-t border-neutral-100">
          <div className="relative">
            <input
              ref={chatInputRef}
              type="text"
              value={chatInput}
              onChange={(e) => onInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (chatInput.trim() && !isChatLoading) {
                    onSend();
                  }
                }
              }}
              placeholder="Ask anything about this campaign..."
              className="w-full px-4 py-3 pr-12 text-[13px] bg-neutral-50 border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-300 focus:bg-white transition-colors"
              disabled={isChatLoading}
            />
            <button
              onClick={onSend}
              disabled={!chatInput.trim() || isChatLoading}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-neutral-400 hover:text-neutral-600 disabled:opacity-30 disabled:hover:text-neutral-400"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {!isOpen && (
        <button
          onClick={onOpen}
          className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 bg-neutral-900 text-white text-[12px] sm:text-[13px] font-medium rounded-full shadow-lg hover:bg-neutral-800 transition-colors z-40"
        >
          <Sparkles className="w-4 h-4" />
          <span className="hidden sm:inline">Assistant</span>
        </button>
      )}
    </>
  );
};
